import { useNavigate } from 'react-router-dom'
import { Reply } from 'lucide-react'
import { nip19 } from 'nostr-tools'
import { Avatar } from '@/components/Avatar'
import { PostAuthorRow } from '@/components/PostAuthorRow'
import { PostEngagementBar } from '@/components/PostEngagementBar'
import { PostActionsMenu } from '@/components/PostActionsMenu'
import { RichText } from '@/components/RichText'
import { PostEmbedDisplay } from '@/components/PostEmbedDisplay'
import { QuotedPostEmbed } from '@/components/QuotedPostEmbed'
import { FeedEmbedCard } from '@/components/FeedEmbedCard'
import { getPostBridgeInfo } from '@/lib/bridge'
import { ContentWarning } from '@/components/ContentWarning'
import { useAuth } from '@/hooks/useAuth'
import { useParentPost } from '@/hooks/useParentPost'
import { usePlatform } from '@/hooks/usePlatform'
import {
    FullAPContentExpander,
    isTruncatedBridgedPost,
    getBridgedPostDisplayText,
    hasQuoteCardReference,
} from '@/components/FullAPContentExpander'
import { isSelfReferencingBridgedCard } from '@/lib/bridgedContent'
import type { Post } from '@/types/posts'

interface ParentPostPreviewProps {
    /** The parent post URI - used to fetch the post if not provided inline */
    parentUri: string
    /** Relay URL hint from NIP-10 e-tag, used to improve Nostr parent post fetch coverage */
    relayHint?: string
    /** Pre-fetched parent post data - if provided, skips the fetch */
    post?: Post
    onAuthorClick?: (identifier: string) => void
    onPostClick?: (post: Post) => void
    onLike?: (post: Post) => void
    onRepost?: (post: Post) => void
    onQuote?: (post: Post) => void
    onBookmark?: (post: Post) => void
    onDelete?: (post: Post) => void
}

export function ParentPostPreview({
    parentUri,
    relayHint,
    post: inlinePost,
    onAuthorClick,
    onPostClick,
    onLike,
    onRepost,
    onQuote,
    onBookmark,
    onDelete,
}: ParentPostPreviewProps) {
    const navigate = useNavigate()
    const { isAuthenticated, session, currentMastodonHandle, currentPixelfedHandle } = useAuth()
    // Only fetch if inline post is not provided
    const {
        parentPost: fetchedPost,
        isLoading,
        error,
    } = useParentPost(inlinePost ? undefined : parentUri, inlinePost ? undefined : relayHint)
    const { selectedProtocol } = usePlatform()

    // Use inline post if available, otherwise use fetched post
    const parentPost = inlinePost ?? fetchedPost

    // Compute ownership of the parent post
    const isOwnPost = (() => {
        if (!parentPost?.author) return false
        if (parentPost.author.did && session?.did && parentPost.author.did === session.did)
            return true
        const handle = parentPost.author.handle?.toLowerCase()
        if (!handle) return false
        return handle === currentMastodonHandle || handle === currentPixelfedHandle
    })()

    // Only show loading if we're actually fetching (no inline post provided)
    if (!inlinePost && isLoading) {
        return (
            <div className="px-4">
                <div className="animate-pulse flex gap-3">
                    {/* Avatar column with thread line */}
                    <div className="flex flex-col items-center flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-border-light dark:bg-border-dark" />
                        <div className="w-0.5 flex-1 min-h-[60px] bg-border-light dark:bg-border-dark mt-1" />
                    </div>
                    <div className="flex-1 space-y-2 pb-2">
                        <div className="flex items-center gap-2">
                            <div className="h-4 bg-border-light dark:bg-border-dark rounded w-24" />
                            <div className="h-3 bg-border-light dark:bg-border-dark rounded w-20" />
                        </div>
                        <div className="h-4 bg-border-light dark:bg-border-dark rounded w-full" />
                        <div className="h-4 bg-border-light dark:bg-border-dark rounded w-3/4" />
                    </div>
                </div>
            </div>
        )
    }

    if ((!inlinePost && error) || !parentPost) return null

    const nostrNevent = (() => {
        if (parentPost.protocol !== 'nostr' || !parentPost.uri) return null
        const id = parentPost.uri.startsWith('nostr:') ? parentPost.uri.slice(6) : parentPost.uri
        try {
            return nip19.neventEncode({ id })
        } catch {
            return null
        }
    })()

    const bridgeInfo = getPostBridgeInfo(parentPost.author, parentPost.bridgyOriginalUrl)
    const canClick =
        !!onPostClick && (parentPost.isActivityPub ? !!parentPost.uri : !!parentPost.rkey)
    const canEngage = !parentPost.isActivityPub && !!parentPost.cid
    const canDelete = canEngage

    const handleAuthorClick = () => {
        if (bridgeInfo?.isFromAP) {
            // Mastodon user bridged to Bluesky → navigate to AT Protocol profile
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty did is invalid, fall back to handle
            const atIdentifier = parentPost.author?.did || parentPost.author?.handle
            if (atIdentifier) void navigate(`/profile/${atIdentifier}`)
        } else if (bridgeInfo) {
            // Bluesky user bridged to fediverse
            const viewingAsAP = selectedProtocol === 'mastodon' || selectedProtocol === 'pixelfed'
            if (viewingAsAP) {
                // Stay in AP context → navigate to the bridged AP handle
                void navigate(`/profile/${bridgeInfo.bridgedHandle}`)
            } else {
                // Viewing as Bluesky → navigate to AT Protocol profile
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty did is invalid, fall back to handle
                const atIdentifier = parentPost.author?.did || parentPost.author?.handle
                if (atIdentifier) void navigate(`/profile/${atIdentifier}`)
            }
        } else if (onAuthorClick && parentPost.author) {
            onAuthorClick(parentPost.author.handle)
        }
    }

    const handlePostClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!canClick) return
        const target = e.target as HTMLElement
        if (target.closest('button, a, [role="button"], video')) return
        onPostClick?.(parentPost)
    }

    return (
        <div
            className={`px-4 ${canClick ? 'cursor-pointer hover:bg-surface-light/50 dark:hover:bg-surface-dark/50' : ''}`}
            onClick={handlePostClick}
        >
            <div className="flex gap-3">
                {/* Avatar column with thread line */}
                <div className="flex flex-col items-center flex-shrink-0">
                    <button
                        onClick={handleAuthorClick}
                        className="focus:outline-none"
                        disabled={!bridgeInfo && !onAuthorClick}
                    >
                        <Avatar
                            src={parentPost.author?.avatarUrl}
                            alt={parentPost.author?.handle ?? 'user'}
                            size="sm"
                        />
                    </button>
                    {/* Thread line extending down */}
                    <div className="w-0.5 flex-1 bg-border-light dark:bg-border-dark mt-1" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-2">
                    {/* Reply context: show who this parent post was replying to */}
                    {parentPost.isReply && (
                        <div className="flex items-center gap-1 text-text-secondary-light dark:text-text-secondary-dark text-xs mb-0.5 min-w-0">
                            <Reply className="w-3 h-3 flex-shrink-0" />
                            {parentPost.replyParentAuthor ? (
                                <span className="truncate min-w-0">
                                    Replying to{' '}
                                    <button
                                        type="button"
                                        onClick={e => {
                                            e.stopPropagation()
                                            if (onAuthorClick && parentPost.replyParentAuthor) {
                                                onAuthorClick(parentPost.replyParentAuthor.handle)
                                            }
                                        }}
                                        className="hover:underline focus:outline-none"
                                        disabled={!onAuthorClick}
                                    >
                                        {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty/whitespace-only displayName should fall through, not render blank */}
                                        {parentPost.replyParentAuthor.displayName?.trim() ||
                                            `@${parentPost.replyParentAuthor.displayHandle ?? parentPost.replyParentAuthor.handle}`}
                                    </button>
                                </span>
                            ) : (
                                <span>Reply</span>
                            )}
                        </div>
                    )}

                    {/* Author row with actions menu */}
                    <div className="flex items-center justify-between gap-2">
                        <PostAuthorRow
                            author={parentPost.author}
                            createdAt={parentPost.createdAt}
                            protocol={parentPost.protocol}
                            isActivityPub={parentPost.isActivityPub}
                            bridgeInfo={bridgeInfo}
                            onAuthorClick={handleAuthorClick}
                            onNativeHandleClick={() =>
                                navigate(`/profile/${parentPost.author?.handle}`)
                            }
                            onBridgedHandleClick={handleAuthorClick}
                            authorClickDisabled={!bridgeInfo && !onAuthorClick}
                        />

                        <div className="flex items-center">
                            {/* More menu */}
                            {isAuthenticated &&
                                ((isOwnPost && canDelete) ||
                                    parentPost.protocol !== 'nostr' ||
                                    (parentPost.protocol === 'nostr' && !!nostrNevent)) && (
                                    <PostActionsMenu
                                        isOwnPost={isOwnPost}
                                        canDelete={canDelete}
                                        onDelete={() => onDelete?.(parentPost)}
                                        text={parentPost.text}
                                        showCopyUri={
                                            parentPost.protocol !== 'nostr' && !!parentPost.author
                                        }
                                        uri={parentPost.uri}
                                        showAtExplore={
                                            parentPost.protocol !== 'nostr' && !!parentPost.author
                                        }
                                        isActivityPub={parentPost.isActivityPub}
                                        authorHandle={parentPost.author?.handle}
                                        rkey={parentPost.rkey}
                                        nostrNevent={
                                            parentPost.protocol === 'nostr' ? nostrNevent : null
                                        }
                                    />
                                )}
                        </div>
                    </div>

                    {/* Post content — wrapped by content warning when present */}
                    {parentPost.contentWarning?.scope === 'content' ? (
                        <ContentWarning text={parentPost.contentWarning.text}>
                            {parentPost.text && (
                                <div className="mt-1">
                                    <RichText
                                        text={parentPost.text}
                                        facets={parentPost.facets}
                                        className="text-sm text-text-primary-light dark:text-text-primary-dark"
                                    />
                                </div>
                            )}
                            {parentPost.embed && (
                                <div className="mt-2">
                                    <PostEmbedDisplay
                                        embed={parentPost.embed}
                                        contentWarning={parentPost.contentWarning}
                                        isTruncated={isTruncatedBridgedPost(parentPost.text)}
                                        excludeFediverse={isSelfReferencingBridgedCard(parentPost)}
                                    />
                                </div>
                            )}
                            {parentPost.quotedPost && (
                                <div className="mt-2">
                                    <QuotedPostEmbed
                                        post={parentPost.quotedPost}
                                        onAuthorClick={onAuthorClick}
                                        onPostClick={onPostClick}
                                    />
                                </div>
                            )}
                            {parentPost.feedEmbed && (
                                <div className="mt-2">
                                    <FeedEmbedCard
                                        feed={parentPost.feedEmbed}
                                        onClick={uri =>
                                            navigate(`/feed/${encodeURIComponent(uri)}`)
                                        }
                                    />
                                </div>
                            )}
                        </ContentWarning>
                    ) : (
                        <>
                            {/* Post text — FullAPContentExpander handles truncated bridged posts */}
                            {parentPost.text && (
                                <div className="mt-1">
                                    <FullAPContentExpander
                                        bridgyOriginalUrl={parentPost.bridgyOriginalUrl}
                                        text={parentPost.text}
                                        hasQuoteReference={hasQuoteCardReference(parentPost)}
                                    >
                                        <RichText
                                            text={getBridgedPostDisplayText(parentPost)}
                                            facets={parentPost.facets}
                                            className="text-sm text-text-primary-light dark:text-text-primary-dark"
                                        />
                                    </FullAPContentExpander>
                                </div>
                            )}

                            {/* Embed — hide "Original post on..." embed on truncated bridged posts */}
                            {parentPost.embed && (
                                <div className="mt-2">
                                    <PostEmbedDisplay
                                        embed={parentPost.embed}
                                        contentWarning={parentPost.contentWarning}
                                        isTruncated={isTruncatedBridgedPost(parentPost.text)}
                                        excludeFediverse={isSelfReferencingBridgedCard(parentPost)}
                                    />
                                </div>
                            )}

                            {/* Quoted post */}
                            {parentPost.quotedPost && (
                                <div className="mt-2">
                                    <QuotedPostEmbed
                                        post={parentPost.quotedPost}
                                        onAuthorClick={onAuthorClick}
                                        onPostClick={onPostClick}
                                    />
                                </div>
                            )}

                            {/* Feed embed */}
                            {parentPost.feedEmbed && (
                                <div className="mt-2">
                                    <FeedEmbedCard
                                        feed={parentPost.feedEmbed}
                                        onClick={uri =>
                                            navigate(`/feed/${encodeURIComponent(uri)}`)
                                        }
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {/* Action bar */}
                    <div className="mt-2">
                        <PostEngagementBar
                            layout="split"
                            size="sm"
                            disabled={!canEngage}
                            isActivityPub={parentPost.isActivityPub}
                            replyCount={parentPost.metrics.replyCount}
                            repostCount={parentPost.metrics.repostCount}
                            isReposted={!!parentPost.repostUri}
                            onRepost={onRepost ? () => onRepost(parentPost) : undefined}
                            canQuote={!!onQuote && canEngage}
                            onQuote={onQuote && canEngage ? () => onQuote(parentPost) : undefined}
                            likeCount={parentPost.metrics.likeCount}
                            isLiked={!!parentPost.likeUri}
                            onLike={onLike ? () => onLike(parentPost) : undefined}
                            isBookmarked={!!parentPost.bookmarkUri}
                            onBookmark={onBookmark ? () => onBookmark(parentPost) : undefined}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
